// The chosen-paper picker (chosen_papers/new): tick a whole heading, tick by
// standing, and keep the summary and the two numbers in step with the ticks.
//
// The page works without this. The form posts the ticked boxes and the server
// (ChosenPaperPool, via ChosenPapersController) decides everything; the
// heading ticks and quick picks ship `hidden` because without a script they
// could do nothing. Every sentence is a template on the summary element and
// every number a data attribute on the form, so nothing here types copy or a
// figure — and the share is an estimate the server has the last word on (an
// item tagged to two lines is counted twice here, once there).

function setup() {
  const form = document.querySelector("[data-chosen-picker]");
  if (!form) return;

  const d = form.dataset;
  const num = (value) => Number(value);
  const boxes = () => Array.from(form.querySelectorAll("input.chosen-paper__box:not(:disabled)"));
  const summary = form.querySelector("[data-summary]");
  const countField = form.querySelector("[data-count]");
  const minutesField = form.querySelector("[data-minutes]");
  // Untouched until the student types: the server's own figures stand on
  // load, and a tick re-derives the default until a number is typed.
  let countTouched = false;

  const clamp = (value, low, high) => Math.min(Math.max(value, low), high);
  const fill = (template, values) =>
    template.replace(/%\{(\w+)\}/g, (_, key) => String(values[key]));

  // Round-robin, as ChosenPaperPool::Report#allocation shares a paper.
  function floorFor(stocks, total) {
    const shares = stocks.map(() => 0);
    let placed = 0;
    while (placed < total) {
      let progressed = false;
      for (let i = 0; i < stocks.length && placed < total; i += 1) {
        if (shares[i] < stocks[i]) {
          shares[i] += 1;
          placed += 1;
          progressed = true;
        }
      }
      if (!progressed) break;
    }
    return Math.min(...shares);
  }

  function recompute() {
    const ticked = boxes().filter((box) => box.checked);
    if (ticked.length === 0) {
      summary.textContent = summary.dataset.empty;
      if (!countTouched) countField.value = "";
      minutesField.placeholder = "";
      return;
    }

    const stocks = ticked.map((box) => num(box.dataset.stock));
    const maxItems = Math.min(stocks.reduce((a, b) => a + b, 0), num(d.maxItems));
    const fallback = Math.min(clamp(ticked.length * num(d.perLine), num(d.defaultMin), num(d.defaultMax)), maxItems);
    const items = countTouched && countField.value !== ""
      ? clamp(Math.round(num(countField.value)) || fallback, 1, maxItems)
      : fallback;
    if (!countTouched) countField.value = items;
    countField.max = maxItems;

    // A suggestion, never a value: the box posts only what the student typed,
    // and a blank one is sized by the server off the count it actually draws.
    minutesField.placeholder = clamp(Math.ceil(num(d.pace) * items), num(d.minMinutes), num(d.maxMinutes));

    const floor = floorFor(stocks, items);
    const noun = (pair, n) => pair.split("|")[n === 1 ? 0 : 1];
    const counts = fill(summary.dataset.counts, {
      keys: ticked.length, items,
      key_noun: noun(summary.dataset.keyNoun, ticked.length),
      item_noun: noun(summary.dataset.itemNoun, items)
    });
    const verdict = floor < num(d.minGroup)
      ? fill(summary.dataset.thin, { min: d.minGroup })
      : fill(summary.dataset.enough, { floor });
    summary.textContent = `${counts}. ${verdict}`;
  }

  // A whole heading, ticked or cleared in one go, and shown as partly ticked
  // when some of its lines are.
  const groups = Array.from(form.querySelectorAll("[data-heading-group]")).map((group) => {
    const tick = group.querySelector("[data-heading-tick]");
    const headBox = group.querySelector("[data-heading-box]");
    const lines = Array.from(group.querySelectorAll("input.chosen-paper__box:not(:disabled)"));
    if (lines.length === 0) return null;

    tick.hidden = false;
    headBox.addEventListener("change", () => {
      lines.forEach((box) => { box.checked = headBox.checked; });
      sync();
    });
    return { headBox, lines };
  }).filter(Boolean);

  function syncHeadings() {
    groups.forEach(({ headBox, lines }) => {
      const on = lines.filter((box) => box.checked).length;
      headBox.checked = on === lines.length;
      headBox.indeterminate = on > 0 && on < lines.length;
    });
  }

  function sync() {
    syncHeadings();
    recompute();
  }

  const picks = form.querySelector("[data-quick-picks]");
  if (picks) {
    picks.hidden = false;
    picks.querySelectorAll("[data-pick]").forEach((button) => {
      const ids = new Set(button.dataset.pick.split(" "));
      button.addEventListener("click", () => {
        boxes().forEach((box) => { if (ids.has(box.value)) box.checked = true; });
        sync();
      });
    });
    const clear = picks.querySelector("[data-pick-clear]");
    clear?.addEventListener("click", () => {
      boxes().forEach((box) => { box.checked = false; });
      countTouched = false;
      sync();
    });
  }

  boxes().forEach((box) => box.addEventListener("change", sync));
  countField.addEventListener("input", () => { countTouched = countField.value !== ""; recompute(); });

  syncHeadings();
}

// The guard every module in this pack carries — see mock_request_wizard.js.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setup);
} else {
  setup();
}
